import React, {
  useState, useEffect, useRef, useMemo,
} from "react";
import Scrollbar from "react-scrollbars-custom";
import { toast } from "react-toastify";
import { Table } from "react-bootstrap";
import moment from "moment";
import {
  Input,
  Button,
  Modal,
  Tooltip,
  Dropdown,
  Spin,
  Select,
} from "antd";
import { EditOutlined, EllipsisOutlined, LoadingOutlined } from "@ant-design/icons";
import ConfirmationModal from "../../../Common/ConfirmationModal";
import { getUser } from "../../../../utils/helpers";
import CustomDebounceSearch from "../../../Common/ChemBioPending/Components/CustomDebounceSearch";
import ReportCommentModal from "./ReportCommentModal";
import StatusTag from "../../../Common/components/StatusTag";

import "./SendReportsPreview.css";

export default function SendReportsPreview(props) {
  const {
    togglePreview,
    previewFile,
    currWoId,
    updateResultApi,
    generateReport,
    enableEdit,
    updateStatusApi,
    handleApproveRejectSampleQC,
    tab,
    woStatus,
    testCategories,
  } = props;
  const [sampleTableData, setSampleTableData] = useState([]);
  const [filteredSampleTableData, setFilteredSampleTableData] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const [resultLoading, setResultLoading] = useState(false);
  const [reportLoading, setReportLoading] = useState(false);
  const [approveType, setApproveType] = useState("");
  const [enableGenerate, setEnableGenerate] = useState(false);
  const [confirmModal, setConfirmModal] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(testCategories[0].value);
  const [searchTerm, setSearchTerm] = useState("");
  const [qcStatusLoadingMap, setQcStatusLoadingMap] = useState(new Map());
  const [reportCommentModalVisible, setReportCommentModalVisible] = useState(false);
  const [selectedActionOption, setSelectedActionOption] = useState(null);
  const textareaRefs = useRef([]);
  const user = getUser();

  /**
   * Applies both search and category filters to the sample table data
   * @param {string} search - The search term to filter by test display name
   * @param {string} category - The category to filter by test category
   * @returns {void} Updates the filteredSampleTableData state with the filtered results
   */
  const applyFilters = (search, category) => {
    setFilteredSampleTableData(
      sampleTableData.filter((item) => {
        const matchesSearch = search === "" || item.display_name.toLowerCase().includes(search.toLowerCase());
        const matchesCategory = category === testCategories[0].value || item.test_category.toLowerCase() === category.toLowerCase();
        return matchesSearch && matchesCategory;
      }),
    );
  };

  // const sampleColumns = [
  //   { header: "Sample ID", param: "sample_id" },
  //   { header: "Sample Type", param: "sample_type" },
  //   { header: "Test", param: "display_name" },
  //   { header: "Result", param: "result" },
  //   { header: "Reason", param: "qc_status" },
  //   { header: "ML QC Result", param: "ml_qc_result", permission: "approve_reject_ml_qc_sendreports" },
  //   { header: "ML QC Message", param: "ml_qc_status", permission: "approve_reject_ml_qc_sendreports" },
  //   { header: "Status", param: "user_correction", permission: "approve_reject_ml_qc_sendreports" },
  //   {
  //     header: "Action", param: "action", permission: "approve_reject_ml_qc_sendreports", className: "SendReportsPreviewTable__ActionCell",
  //   },
  // ];

  const qcSampleColumns = [
    { header: "Sample", param: "sample" },
    { header: "Test", param: "test" },
    { header: "Manual QC", param: "manual_qc" },
    { header: "QC", param: "qc", className: "SendReportPreview__QCCell" },
    // { header: "Software QC", param: "software_qc" },
    // { header: "AI QC", param: "ml_qc", permission: "approve_reject_ml_qc_sendreports" },
  ];

  // const authorizedSampleColumns = sampleColumns.filter((item) => !item.permission || user.permissionMap[item.permission]);
  const authorizedSampleColumns = qcSampleColumns.filter((item) => !item.permission || user.permissionMap[item.permission]);

  const userCorrectionMap = {
    1: "Approved",
    2: "Rejected",
    Approved: 1,
    Rejected: 2,
  };

  const mlQCResultMap = {
    1: "Passed",
    2: "Failed",
  };

  const actionItems = [
    {
      label: "Approve",
      key: "Approved",
    },
    {
      label: "Reject",
      key: "Rejected",
    },
  ];

  const makeReasonArray = (reason) => {
    let inputString = reason;
    while (inputString.charAt(0) === "\n") {
      inputString = inputString.slice(1);
    }
    while (inputString.charAt(inputString.length - 1) === "\n") {
      inputString = inputString.slice(0, -1);
    }
    return inputString.split("\n");
  };

  /**
   * Set all the action menu loading to false
   */
  const initialQcStatusLoadMap = () => {
    const statusMap = new Map();
    previewFile.sample_info.forEach((sample) => {
      statusMap.set(sample.tis_pk, false);
    });
    setQcStatusLoadingMap(statusMap);
  };

  /**
   * Update a loading state for a test in the Action menu column in the Sample Information table
   */
  const setQcStatusLoading = (sample, loading) => {
    setQcStatusLoadingMap((prev) => {
      const newMap = new Map(prev);
      newMap.set(sample.tis_pk, loading);
      return newMap;
    });
  };

  /**
   * Set the table data and all the edit states
   */
  useEffect(() => {
    // Initialize Sample Information data and the loading state
    textareaRefs.current = previewFile.sample_info.map(() => React.createRef());
    const newData = previewFile.sample_info.map((obj) => ({
      ...obj, edit: false,
    }));
    setSampleTableData(newData);
    setFilteredSampleTableData(newData);
    initialQcStatusLoadMap();
  }, []); //eslint-disable-line

  /**
   * Add an EventListener to handle the outside click event to close the result input
   * 1. Prevent the outside click close the input if any of api calls is processing
   * 2. Outside click need to reset the input value and the edit state to false.
   */
  useEffect(() => {
    const handleClickOutside = (event) => {
      const updateIndex = [];
      filteredSampleTableData.forEach((data, index) => {
        if (
          data.edit
          && textareaRefs.current[index].current
          && !textareaRefs.current[index].current.contains(event.target)
          && !resultLoading
          && !reportLoading
        ) {
          updateIndex.push(index);
        }
      });
      if (updateIndex.length > 0) {
        setSampleTableData((prevData) => {
          const newData = [...prevData];
          updateIndex.forEach((idx) => {
            const parentIdx = newData.findIndex((parentItem) => parentItem.tis_pk === filteredSampleTableData[idx].tis_pk);
            if (parentIdx > -1) {
              newData[parentIdx] = {
                ...newData[parentIdx],
                edit: false,
              };
            }
          });
          return newData;
        });
        setFilteredSampleTableData((prevData) => {
          const newData = [...prevData];
          updateIndex.forEach((idx) => {
            newData[idx] = { ...newData[idx], edit: false };
          });
          return newData;
        });
        setInputValue(() => "");
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [filteredSampleTableData, resultLoading, reportLoading]);

  /**
   * Enable the current test's result editing
   * @param {*} e
   * @param {*} index
   * @returns
   */
  const handleEditResult = (e, index) => {
    e.stopPropagation();
    // prevent open the input if any of api calls is processing
    if (reportLoading) {
      toast.warn("Please wait for the current process to finish.");
      return;
    }
    setFilteredSampleTableData((prevData) => {
      const newData = [...prevData];
      newData[index] = { ...newData[index], edit: true };
      return newData;
    });
    // set the input value to the current result value
    setInputValue(() => filteredSampleTableData[index].result);
  };

  /**
   * Make an api call to update the test result
   * @param {*} index
   * @returns
   */
  const handleUpdateResult = async (index) => {
    // input value cannot be empty
    if (inputValue === "") {
      toast.warn("Please enter a valid Result value");
      return;
    }
    const payload = {
      GenistaID: filteredSampleTableData[index].genista_id,
      Analysis: filteredSampleTableData[index].internal_name,
      Result: inputValue,
      OrderID: currWoId,
      overwrite_complete: true,
      source: `Automation - Send Reports -${tab}`,
    };
    setResultLoading(() => true);
    const resp = await updateResultApi(payload);
    setResultLoading(() => false);
    /** If update succeeded, then reset the input value and close the input */
    if (resp.success) {
      setSampleTableData((prevData) => {
        const newData = [...prevData];
        const parentIdx = newData.findIndex((parentItem) => parentItem.tis_pk === filteredSampleTableData[index].tis_pk);
        if (parentIdx > -1) {
          newData[parentIdx] = {
            ...newData[parentIdx],
            edit: false,
            result: inputValue,
          };
        }
        return newData;
      });
      setFilteredSampleTableData((prevData) => {
        const newData = [...prevData];
        newData[index] = {
          ...newData[index],
          edit: false,
          result: inputValue,
        };
        return newData;
      });
      setInputValue(() => "");
      setEnableGenerate(true);
    }
  };

  const handleRegenerateClick = () => {
    if (reportLoading) {
      toast.warn("Please wait for the current process to finish.");
      return;
    }
    setConfirmModal(true);
  };
  /**
   * Make an api call to re-generate a report for the current workorder
   * @returns
   */
  const handleRegenerateReport = async () => {
    // If any of the updateresult/ is processing, prevent the generate report api call
    if (resultLoading) {
      toast.warn("Please wait for the current process to finish.");
      return;
    }
    setReportLoading(true);
    await generateReport();
    setReportLoading(false);
  };

  /**
   * Close the modal. If updateresult/ or generatebatchreport/ is processing, then prevent the closing.
   */
  const handleCancelModal = () => {
    // If one of the Approve/Reject action is in progress, prevent user from closing the modal
    if (sampleTableData.find((sample) => qcStatusLoadingMap.get(sample.tis_pk))) return;

    // Remind users to regenerate a report to update to the result changes on a report.
    // If a work order has multiple reports, then don't force user to regenerate the report
    if (enableGenerate && woStatus.get(previewFile.wo).totalReports === 1) {
      toast.warn("The results have been edited. Please click the 'Regenerate Report' button to update the report.");
      return;
    }
    if (!resultLoading && !reportLoading) {
      togglePreview({ ...previewFile, sample_info: sampleTableData });
    }
  };

  /**
   * Make an api call to update the current report send status
   */
  const handleMarkStatus = async (approve_type) => {
    setReportLoading(true);
    setApproveType(approve_type);
    const { success } = await updateStatusApi({ ...previewFile, approve_type });
    if (!success) {
      setApproveType(previewFile.approve_status);
    }
    setReportLoading(false);
  };

  /**
   * Update a test's user correction value
   */
  const updateSampleQCStatus = async (option, element, { comment }) => {
    setQcStatusLoading(element, true);
    const newSample = {
      ...element,
      user_correction: userCorrectionMap[option.key],
    };
    const { success, result } = await handleApproveRejectSampleQC(newSample, comment);
    setQcStatusLoading(element, false);
    // If API succeeds and backend returns a valid result object, then we update the latest user correction value in the Sample Information table
    if (success && typeof result === "object" && result !== null && !Array.isArray(result)) {
      const {
        corrected_by, corrected_at, user_correction, ml_qc_comments,
      } = result;
      // Update cached data
      setSampleTableData((prev) => {
        const newData = [...prev];
        newData.forEach((newItem) => {
          if (newItem.tis_pk === element.tis_pk) {
            newItem.user_correction = user_correction;
            newItem.corrected_by = corrected_by;
            newItem.corrected_at = corrected_at;
            newItem.ml_qc_comments = ml_qc_comments;
          }
        });
        return newData;
      });
      // Update filter data
      setFilteredSampleTableData((prev) => {
        const newData = [...prev];
        newData.forEach((newItem) => {
          if (newItem.tis_pk === element.tis_pk) {
            newItem.user_correction = user_correction;
            newItem.corrected_by = corrected_by;
            newItem.corrected_at = corrected_at;
            newItem.ml_qc_comments = ml_qc_comments;
          }
        });
        return newData;
      });
    }
  };

  const updateApproveReject = async (data) => {
    if (!selectedActionOption) return;
    const { menuOption, element } = selectedActionOption;
    await updateSampleQCStatus(menuOption, element, { comment: data });
    setSelectedActionOption(null);
    setReportCommentModalVisible(false);
  };

  const onTestSearch = (event) => {
    const value = event.target.value;
    setSearchTerm(value);
    applyFilters(value, selectedCategory);
  };

  const filterByTestCategory = (value) => {
    const category = value || testCategories[0].value;
    setSelectedCategory(category);
    applyFilters(searchTerm, category);
  };

  const getSubmitButtonLoading = useMemo(() => {
    if (!selectedActionOption) return false;
    const { element: sample } = selectedActionOption;
    return qcStatusLoadingMap.get(sample.tis_pk) || false;
  }, [selectedActionOption, qcStatusLoadingMap]);

  const getCommentModalTitle = useMemo(() => {
    if (!selectedActionOption) return "Add comment";
    const isApproval = selectedActionOption.menuOption.key === "Approved";
    return `Add comment for ${isApproval ? "approval" : "rejection"}`;
  }, [selectedActionOption]);

  const sampleInformationTab = () => (
    <>
      <div className="SendReportsPreview__SampleTableSearchBar">
        <CustomDebounceSearch
          placeholder="Search by Test"
          className="SendReportsPreviewSearch"
          onSearch={onTestSearch}
        />
        <Select
          allowClear
          showSearch
          className="SendReportsPreview__TestCategorySelect"
          options={testCategories}
          defaultValue={testCategories[0]?.value}
          value={selectedCategory}
          onSelect={filterByTestCategory}
          onClear={() => filterByTestCategory("")}
          filterOption={(optionValue, option) => option.value?.toLowerCase().startsWith(optionValue?.toLowerCase())}
          listHeight={400}
        />
      </div>
      <Scrollbar
        className="SendReportsPreview__Scrollbar"
        disableTrackYWidthCompensation
        disableTracksWidthCompensation
      >

        <Table bordered hover className="table table-fixed sendReportPreviewTestsTable SentReportsPreview__TestsTable">
          <thead>
            <tr className="SentReportsPreview__TestsTableHeading">
              {authorizedSampleColumns.map((item, key) => (<td key={key} className={item.className || ""}>{item.header}</td>))}
            </tr>
          </thead>
          <tbody id="sentReportPreviewTestsTbody" className="SentReportsPreview__TestsTableBody">
            {filteredSampleTableData.map((element, index) => (
              <tr key={index} className="SendReportsPreview__Table__Tr">
                {authorizedSampleColumns.map((item, key) => {
                  const { param } = item;
                  const { tis_pk } = element;
                  let childNode = element[item.param] || "-";
                  const className = item.className || "";
                  // const className = sampleColumns.find((col) => col.param === param)?.className;
                  if (param === "sample") {
                    childNode = (
                      <div className="SendReportPreview__SampleCell">
                        <p className="SendReportPreview__SampleCell--TextDark">{element.sample_id}</p>
                        <p className="SendReportPreview__SampleCell-TextLight">
                          {`Sample Type: ${element.sample_type}`}
                        </p>
                      </div>
                    );
                  }

                  if (param === "qc") {
                    const reasons = makeReasonArray(element.qc_status);
                    const { corrected_by, corrected_at, ml_qc_comments } = element;

                    const statusTooltipTitle = corrected_by && corrected_at ? () => (
                      <span>
                        {corrected_by}
                        <br />
                        {moment(corrected_at).format(
                          "MM/DD/YYYY, h:mm:ss a",
                        )}
                        <br />
                        {`Comment: ${ml_qc_comments}`}
                      </span>
                    ) : "";

                    const menuItems = element.user_correction !== "" ? actionItems.filter((actionItem) => actionItem.key !== userCorrectionMap[element.user_correction]) : actionItems;

                    childNode = (
                      <div>
                        <div className="SendReportPreview__SampleCell">
                          <div className="SendReportPreview__QCStatusLabelContainer">
                            <span className="SendReportPreview__SampleCell--TextDark QCLabel">Software QC:</span>
                            <StatusTag result={element.qc_result} type="QC" />
                          </div>
                          {reasons.length ? reasons.map((reason, index_r) => (
                            <p key={index_r} className="SendReportPreview__SampleCell-TextLight">
                              {reason || "-"}
                            </p>
                          )) : "-"}
                        </div>
                        {user.permissionMap.approve_reject_ml_qc_sendreports && (
                        <Tooltip
                          placement="top"
                          title={statusTooltipTitle}
                          key={key}
                        >
                          <div className="SendReportPreview__SampleCell">
                            <div className="SendReportPreview__SampleCell__MLQCStatus">
                              <div className="SendReportPreview__QCStatusLabelContainer">
                                <span className="SendReportPreview__SampleCell--TextDark QCLabel">AI QC:</span>
                                <StatusTag result={element.ml_qc_result} type="QC">
                                  {element.user_correction !== "" && (
                                  <span className="SendReportPreview__UserCorrectionText">
                                    {" "}
                                    -
                                    {" "}
                                    {userCorrectionMap[element.user_correction]}
                                  </span>
                                  )}
                                </StatusTag>
                              </div>
                              {qcStatusLoadingMap.get(tis_pk) ? <Spin className="SendReportsPreviewActionSpin" indicator={<LoadingOutlined spin />} size="medium" /> : (
                                <Dropdown
                                  type="primary"
                                  menu={{
                                    items: menuItems,
                                    // onClick: (menuOption) => updateSampleQCStatus(menuOption, element),
                                    onClick: (menuOption) => {
                                      setSelectedActionOption({
                                        menuOption,
                                        element,
                                        currentStatus: userCorrectionMap[element.user_correction],
                                      });
                                      setReportCommentModalVisible(true);
                                    },
                                  }}
                                >
                                  <EllipsisOutlined className="SendReportsPreview__ActionIcon" />
                                </Dropdown>
                              )}
                            </div>

                            <p className="SendReportPreview__SampleCell-TextLight">
                              {element.ml_qc_status || "-"}
                            </p>
                          </div>
                        </Tooltip>
                        )}
                      </div>
                    );
                  }

                  if (param === "test") {
                    let resultChildNode;
                    if (element.qc_result === 2 || element.qc_result === 3) {
                      if (element.edit) {
                        resultChildNode = (
                          <div
                            ref={textareaRefs.current[index]}
                            className="SendReportsPreview__Result__Textarea"
                          >
                            <Input.TextArea
                              autoSize
                              defaultValue={element.result}
                              maxLength={300}
                              onChange={(e) => setInputValue(e.target.value)}
                            />
                            <Button
                              type="primary"
                              className="SendReportsPreview__SaveBtn"
                              onClick={() => handleUpdateResult(index)}
                              loading={resultLoading}
                              disabled={inputValue === element.result}
                            >
                              Save
                            </Button>
                          </div>
                        );
                      } else {
                        resultChildNode = (
                          <div className="SendReportsPreview__Result">
                            <p className="SendReportPreview__SampleCell-TextLight">
                              {element.result || "-"}
                            </p>
                            {enableEdit && (
                              <EditOutlined
                                onClick={(e) => handleEditResult(e, index)}
                                className="SendReportsPreview__Result__Edit"
                              />
                            )}
                          </div>
                        );
                      }
                    } else {
                      resultChildNode = (
                        <div className="SendReportsPreview__Result">
                          <p className="SendReportPreview__SampleCell-TextLight">
                            {element.result || "-"}
                          </p>
                        </div>
                      );
                    }
                    childNode = (
                      <Tooltip
                        placement="top"
                        title={`Test Category: ${element.test_category}`}
                        key={key}
                      >
                        <p className="SendReportPreview__SampleCell--TextDark">
                          {element.display_name || "-"}
                        </p>
                        {resultChildNode}
                      </Tooltip>
                    );
                  }

                  if (param === "software_qc") {
                    const reasons = makeReasonArray(element.qc_status);
                    childNode = (
                      <div className="SendReportPreview__SampleCell">
                        <StatusTag result={element.qc_result} type="QC" />
                        {reasons.length ? reasons.map((reason, index_r) => (
                          <p key={index_r} className="SendReportPreview__SampleCell-TextLight">
                            {reason || "-"}
                          </p>
                        )) : "-"}
                      </div>
                    );
                  }

                  if (param === "ml_qc") {
                    const { corrected_by, corrected_at, ml_qc_comments } = element;

                    const statusTooltipTitle = corrected_by && corrected_at ? () => (
                      <span>
                        {corrected_by}
                        <br />
                        {moment(corrected_at).format(
                          "MM/DD/YYYY, h:mm:ss a",
                        )}
                        <br />
                        {`Comment: ${ml_qc_comments}`}
                      </span>
                    ) : "";

                    const menuItems = element.user_correction !== "" ? actionItems.filter((actionItem) => actionItem.key !== userCorrectionMap[element.user_correction]) : actionItems;
                    childNode = (
                      <Tooltip
                        placement="top"
                        title={statusTooltipTitle}
                        key={key}
                      >
                        <div className="SendReportPreview__SampleCell">
                          <div className="SendReportPreview__SampleCell__MLQCStatus">
                            <StatusTag result={element.ml_qc_result} type="QC">
                              {element.user_correction !== "" && (
                                <span className="SendReportPreview__UserCorrectionText">
                                  {" "}
                                  -
                                  {" "}
                                  {userCorrectionMap[element.user_correction]}
                                </span>
                              )}
                            </StatusTag>
                            {qcStatusLoadingMap.get(tis_pk) ? <Spin className="SendReportsPreviewActionSpin" indicator={<LoadingOutlined spin />} size="medium" /> : (
                              <Dropdown
                                type="primary"
                                menu={{
                                  items: menuItems,
                                  // onClick: (menuOption) => updateSampleQCStatus(menuOption, element),
                                  onClick: (menuOption) => {
                                    setSelectedActionOption({
                                      menuOption,
                                      element,
                                      currentStatus: userCorrectionMap[element.user_correction],
                                    });
                                    setReportCommentModalVisible(true);
                                  },
                                }}
                              >
                                <EllipsisOutlined className="SendReportsPreview__ActionIcon" />
                              </Dropdown>
                            )}
                          </div>

                          <p className="SendReportPreview__SampleCell-TextLight">
                            {element.ml_qc_status || "-"}
                          </p>
                        </div>
                      </Tooltip>
                    );
                  }

                  if (param === "ml_qc_result" && element[item.param]) {
                    childNode = mlQCResultMap[element[item.param]];
                  }

                  if (param === "manual_qc") {
                    const { manual_qc_by, manual_qc_at } = element;

                    const statusTooltipTitle = manual_qc_by && manual_qc_at ? () => (
                      <span>
                        {manual_qc_by}
                        <br />
                        {moment(manual_qc_at).format(
                          "MM/DD/YYYY, h:mm:ss a",
                        )}
                      </span>
                    ) : "";

                    childNode = (
                      <Tooltip
                        placement="top"
                        title={statusTooltipTitle}
                        key={key}
                      >
                        <div className="SendReportPreview__SampleCell">
                          <StatusTag result={element.manual_qc_result} type="MANUAL_QC" />
                          <p className="SendReportPreview__SampleCell-TextLight">
                            {element.manual_qc_status || "-"}
                          </p>
                        </div>
                      </Tooltip>
                    );
                  }

                  return (<td key={key} className={className}>{childNode}</td>);
                })}
              </tr>
            ))}
          </tbody>
        </Table>
        {/* <Table striped bordered hover className="table table-fixed sendReportPreviewTestsTable">
          <thead>
            <tr className="sentReportsPreviewTestsTableHeading">
              {authorizedSampleColumns.map((item, key) => (<td key={key} className={item.className || ""}>{item.header}</td>))}
            </tr>
          </thead>
          <tbody id="sentReportPreviewTestsTbody">
            {filteredSampleTableData.map((element, index) => (
              <tr key={index} className="SendReportsPreview__Table__Tr">
                {authorizedSampleColumns.map((item, key) => {
                  const { param } = item;
                  const { tis_pk } = element;
                  let childNode = element[item.param] || "-";
                  const className = sampleColumns.find((col) => col.param === param)?.className;
                  if (param === "display_name") {
                    childNode = (
                      <Tooltip
                        placement="top"
                        title={`Test Category: ${element.test_category}`}
                        key={key}
                      >
                        {element[item.param] || "-"}
                      </Tooltip>
                    );
                  }
                  if (param === "result") {
                    if (element.qc_result === 2 || element.qc_result === 3) {
                      if (element.edit) {
                        childNode = (
                          <div
                            ref={textareaRefs.current[index]}
                            className="SendReportsPreview__Result__Textarea"
                          >
                            <Input.TextArea
                              autoSize
                              defaultValue={element.result}
                              maxLength={300}
                              onChange={(e) => setInputValue(e.target.value)}
                            />
                            <Button
                              type="primary"
                              className="SendReportsPreview__SaveBtn"
                              onClick={() => handleUpdateResult(index)}
                              loading={resultLoading}
                              disabled={inputValue === element.result}
                            >
                              Save
                            </Button>
                          </div>
                        );
                      } else {
                        childNode = (
                          <div className="SendReportsPreview__Result">
                            {element.result}
                            {enableEdit && (
                              <EditOutlined
                                onClick={(e) => handleEditResult(e, index)}
                                className="SendReportsPreview__Result__Edit"
                              />
                            )}
                          </div>
                        );
                      }
                    }
                  }
                  if (param === "qc_status") {
                    const reasons = makeReasonArray(element.qc_status);
                    childNode = reasons.map((reason, index_r) => (
                      <div key={index_r} className="SendReportsPreview__Reason">
                        &#8226;
                        <p className="SendReportsPreview__Reason_p">
                          {reason}
                        </p>
                      </div>
                    ));
                  }
                  if (param === "user_correction" && element[item.param] !== "") {
                    const { corrected_by, corrected_at, ml_qc_comments } = element;
                    const statusTooltipTitle = corrected_by && corrected_at ? () => (
                      <span>
                        {corrected_by}
                        <br />
                        {moment(corrected_at).format(
                          "MM/DD/YYYY, h:mm:ss a",
                        )}
                        <br />
                        {`Comment: ${ml_qc_comments}`}
                      </span>
                    ) : "";
                    childNode = (
                      <Tooltip
                        placement="top"
                        title={statusTooltipTitle}
                        key={key}
                      >
                        {userCorrectionMap[element[item.param]] || "-"}
                      </Tooltip>
                    );
                  }
                  if (param === "ml_qc_result" && element[item.param]) {
                    childNode = mlQCResultMap[element[item.param]];
                  }
                  if (param === "action") {
                    const menuItems = element.user_correction !== "" ? actionItems.filter((actionItem) => actionItem.key !== userCorrectionMap[element.user_correction]) : actionItems;
                    childNode = qcStatusLoadingMap.get(tis_pk) ? <Spin className="SendReportsPreviewActionSpin" indicator={<LoadingOutlined spin />} size="medium" /> : (
                      <Dropdown
                        type="primary"
                        menu={{
                          items: menuItems,
                          // onClick: (menuOption) => updateSampleQCStatus(menuOption, element),
                          onClick: (menuOption) => {
                            setSelectedActionOption({
                              menuOption,
                              element,
                              currentStatus: userCorrectionMap[element.user_correction],
                            });
                            setReportCommentModalVisible(true);
                          },
                        }}
                      >
                        <EllipsisOutlined className="SendReportsPreview__ActionIcon" />
                      </Dropdown>
                    );
                  }

                  return (<td key={key} className={className}>{childNode}</td>);
                })}
              </tr>
            ))}
          </tbody>
        </Table> */}
      </Scrollbar>
      {enableEdit && (
        <div className="SendReportsPreview__GenerateDiv">
          {enableGenerate && (
            <Button
              type="primary"
              onClick={() => handleRegenerateClick()}
            >
              Regenerate Report
            </Button>
          )}
        </div>
      )}
    </>
  );

  return (
    <>
      <Modal
        title=""
        open
        centered
        maskClosable
        footer={null}
        closable={false}
        onCancel={() => handleCancelModal()}
        className="SendReportsPreviewModal"
      >
        <div className="SendReportsPreview">
          <div className="SendReportsPreview__Title">
            <span className="SendReportsPreview__TitleHeader">{previewFile.title}</span>
            {tab !== "history" && (
              <div className="SendReportsPreview__StatusSection">
                {previewFile.approve_status !== 0 && (
                  <Button
                    type="primary"
                    onClick={() => handleMarkStatus(0)}
                    loading={approveType === 0}
                    disabled={reportLoading}
                  >
                    Pending
                  </Button>
                )}

                {previewFile.approve_status !== 1 && (
                  <Button
                    type="primary"
                    onClick={() => handleMarkStatus(1)}
                    loading={approveType === 1}
                    disabled={reportLoading}
                  >
                    Approve
                  </Button>
                )}

                {previewFile.approve_status !== 2 && (
                  <Button
                    type="primary"
                    onClick={() => handleMarkStatus(2)}
                    loading={approveType === 2}
                    disabled={reportLoading}
                  >
                    Hold
                  </Button>
                )}

                {previewFile.approve_status !== 3 && (
                  <Button
                    type="primary"
                    onClick={() => handleMarkStatus(3)}
                    loading={approveType === 3}
                    disabled={reportLoading}
                  >
                    Retesting
                  </Button>
                )}

              </div>
            )}
          </div>
          <div className="SendReportsPreview__Components">
            <iframe
              className="SendReportsPreview__iframe"
              src={`${previewFile.url}#toolbar=0&view=FitH`}
              title="Report"
            />

            <div className="SendReportsPreviewTabContainer">
              {sampleInformationTab()}
            </div>
          </div>
          <ConfirmationModal
            title="Are you sure you want to regenerate this report?"
            bodyText="This will <strong>clear</strong> all previously selected reports."
            open={confirmModal}
            loading={reportLoading}
            okText="Regenerate"
            cancelText="Cancel"
            onOk={() => {
              handleRegenerateReport();
            }}
            onCancel={() => setConfirmModal(false)}
          />
        </div>
      </Modal>
      <ReportCommentModal
        open={reportCommentModalVisible}
        onCancel={() => {
          setSelectedActionOption(null);
          setReportCommentModalVisible(false);
        }}
        title={getCommentModalTitle}
        previousCommentTitle={selectedActionOption ? `${selectedActionOption.currentStatus} comment` : ""}
        previousComment={selectedActionOption ? selectedActionOption.element.ml_qc_comments : ""}
        onSubmit={({ data }) => updateApproveReject(data)}
        loading={getSubmitButtonLoading}
      />
    </>
  );
}
